import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  useEvictorAdvancedConfigMutation,
  useEvictorConfigMutation,
  getEvictorAdvancedConfigQueryOptions,
} from '@cast/shared-workload-eviction-data-access';
import { useConfigureFeatureMutation } from '@cast/split-me/hooks/mutations/kent';
import { getFeaturesConfigsQueryOptions } from '@cast/split-me/hooks/queries/kent';

import {
  buildAutoscalerEvictorConfig,
  mergeAutoscalerEvictionConfigEntries,
  mergeKentConsolidationConfig,
} from './_utils';
import { OptimizationGoal } from '../../open-api-types/autopilot-types';

import {
  useKentrollerCompatabilityWithContinuousRebalancing,
  KentrollerCompatibilityWithContinuousRebalancingState,
} from './useKentrollerCompatabilityWithContinuousRebalancing';
import { Cluster } from '@cast/types';

import { isKentManagedCluster } from '@cast/shared-cluster-context-data-access';
import { notReachable } from '@cast/utils';
import { BinPackingFlowState, KentBinPackingFlowState } from '../../_types';
import { featureTypeToId } from '@cast/split-me/features/kent';

type UseEvictorConfigResult = {
  state: BinPackingFlowState;
  configureBinPacking: (params: {
    optimizationGoal: OptimizationGoal;
    excludedNamespaces: string[];
  }) => Promise<boolean>;
};

const mapKentrollerCompatabilityStateToKentBinPackingFlowState = (
  state: KentrollerCompatibilityWithContinuousRebalancingState
): KentBinPackingFlowState => {
  switch (state.type) {
    case 'checking':
      return { type: 'checking-crb-compatibility' };
    case 'check-error':
      return {
        type: 'checking-crb-compatibility-error',
        error: state.error,
        retry: state.retry,
      };
    case 'compatible':
    case 'incompatible':
      return {
        type: 'checking-crb-compatibility-complete',
        isCompatible: state.type === 'compatible',
      };
    default:
      return notReachable(state);
  }
};

const mapInternalStateToBinPackingFlowState = ({
  isKentManaged,
  kentrollerCompatibilityWithContinuousRebalancingState,
}: {
  isKentManaged: boolean;
  kentrollerCompatibilityWithContinuousRebalancingState: KentrollerCompatibilityWithContinuousRebalancingState;
}): BinPackingFlowState => {
  if (isKentManaged) {
    return {
      mode: 'kent',
      kentBinPackingFlowState:
        mapKentrollerCompatabilityStateToKentBinPackingFlowState(
          kentrollerCompatibilityWithContinuousRebalancingState
        ),
    };
  }
  return { mode: 'autoscaler' };
};

const isKentrollerCompatabilityCheckComplete = (
  kontrollerCompatibilityWithContinuousRebalancingState: KentrollerCompatibilityWithContinuousRebalancingState
): boolean => {
  switch (kontrollerCompatibilityWithContinuousRebalancingState.type) {
    case 'compatible':
    case 'incompatible':
      return true;
    case 'checking':
    case 'check-error':
      return false;
    default:
      return notReachable(
        kontrollerCompatibilityWithContinuousRebalancingState
      );
  }
};

export const useBinPackingFlow = ({
  cluster,
  organizationId,
}: {
  cluster: Cluster;
  organizationId: string;
}): UseEvictorConfigResult => {
  const clusterId = cluster.id;
  const isKentManaged = isKentManagedCluster(cluster);
  const queryClient = useQueryClient();
  const { mutateAsync: updateAutoscalerEvictorConfig } =
    useEvictorConfigMutation();
  const { mutateAsync: updateAutoscalerEvictorAdvancedConfig } =
    useEvictorAdvancedConfigMutation();
  const { mutateAsync: configureKentFeature } = useConfigureFeatureMutation();

  const kentrollerCompatibilityWithContinuousRebalancingState =
    useKentrollerCompatabilityWithContinuousRebalancing({
      cluster,
      enabled: isKentManaged,
    });

  const configureBinPacking = useCallback(
    async ({
      optimizationGoal,
      excludedNamespaces,
    }: {
      optimizationGoal: OptimizationGoal;
      excludedNamespaces: string[];
    }): Promise<boolean> => {
      if (isKentManaged) {
        if (
          !isKentrollerCompatabilityCheckComplete(
            kentrollerCompatibilityWithContinuousRebalancingState
          )
        ) {
          return false;
        }

        if (
          kentrollerCompatibilityWithContinuousRebalancingState.type ===
          'incompatible'
        ) {
          // when kentroller is incompatible, we return success to avoid showing an error to the user
          return true;
        }

        try {
          const featuresConfigs = await queryClient.fetchQuery(
            getFeaturesConfigsQueryOptions({
              organizationId,
              clusterId,
              featureId: featureTypeToId('CONTINUOUS_REBALANCING'),
              // Merging over the live config (and retrying after a partial
              // failure) must see the latest state, so bypass the global staleTime.
              staleTime: 0,
            })
          );

          const consolidationFeatureConfig = featuresConfigs?.find(
            (feature) => feature.type === 'CONTINUOUS_REBALANCING'
          );

          await configureKentFeature({
            clusterId,
            featureConfig: {
              continuousRebalancingConfig: mergeKentConsolidationConfig({
                optimizationGoal,
                excludedNamespaces,
                existingConfig: consolidationFeatureConfig,
              }),
            },
          });

          return true;
        } catch {
          return false;
        }
      }

      try {
        await updateAutoscalerEvictorConfig({
          clusterId,
          config: buildAutoscalerEvictorConfig(optimizationGoal),
        });

        const existingAdvancedConfig = await queryClient.fetchQuery(
          getEvictorAdvancedConfigQueryOptions({
            params: { clusterId },
            options: {
              // Merging over the live config (and retrying after a partial
              // failure) must see the latest state, so bypass the global staleTime.
              staleTime: 0,
            },
          })
        );

        await updateAutoscalerEvictorAdvancedConfig({
          clusterId,
          advancedConfig: {
            evictionConfig: mergeAutoscalerEvictionConfigEntries({
              existingEntries: existingAdvancedConfig?.evictionConfig ?? [],
              excludedNamespaces,
            }),
          },
        });

        return true;
      } catch {
        return false;
      }
    },
    [
      kentrollerCompatibilityWithContinuousRebalancingState,
      clusterId,
      organizationId,
      isKentManaged,
      queryClient,
      configureKentFeature,
      updateAutoscalerEvictorConfig,
      updateAutoscalerEvictorAdvancedConfig,
    ]
  );

  return {
    state: mapInternalStateToBinPackingFlowState({
      isKentManaged,
      kentrollerCompatibilityWithContinuousRebalancingState,
    }),
    configureBinPacking,
  };
};
